/* Real campaign deadline + real tracked inventory. No countdown resets or stock simulation. */
(() => {
  if (customElements.get('catarette-deal')) return;
  const inFlight = new Map();
  const FRESH_FOR = 180000;

  async function fetchState(url) {
    const key = url.toString();
    if (inFlight.has(key)) return inFlight.get(key);
    const request = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(key, {cache: 'no-store', credentials: 'same-origin', signal: controller.signal});
        if (!response.ok) throw new Error('Deal refresh failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const node = doc.querySelector('[data-ct-deal-state]');
        if (!node) throw new Error('Missing deal section');
        return JSON.parse(node.dataset.ctDealState);
      } finally {
        clearTimeout(timeout);
      }
    })();
    inFlight.set(key, request);
    try { return await request; } finally { inFlight.delete(key); }
  }

  customElements.define('catarette-deal', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.requestNumber = 0;
      this.pending = false;
      try { this.state = JSON.parse(this.dataset.state); } catch { this.hidden = true; return; }
      this.lastSync = Date.now();
      this.clockOffset = Number(this.state.serverNow) * 1000 - Date.now();
      this.wantedVariant = String(this.state.variantId);
      this.productId = String(this.state.productId);
      const {signal} = this.controller;
      document.addEventListener('variant:change', event => {
        if (!this.dataset.formId || event.detail?.formId !== this.dataset.formId) return;
        this.requestNumber++;
        this.wantedVariant = event.detail.variant?.id ? String(event.detail.variant.id) : '';
        this.pending = true;
        this.hidden = true;
        if (this.wantedVariant) this.refresh(true);
      }, {signal});
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) { this.tick(); this.refresh(true); }
      }, {signal});
      document.addEventListener('cart:change', () => this.refresh(true), {signal});
      window.addEventListener('pageshow', event => { if (event.persisted) this.refresh(true); }, {signal});
      this.applyState(this.state);
      this.clockTimer = setInterval(() => { if (!document.hidden) this.tick(); }, 1000);
      this.refreshTimer = setInterval(() => this.refresh(), 60000);
      this.refresh();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.requestNumber++;
      clearInterval(this.clockTimer);
      clearInterval(this.refreshTimer);
    }

    isVisible() {
      const rect = this.getBoundingClientRect();
      return !document.hidden && rect.top < innerHeight + 500 && rect.bottom > -500;
    }

    async refresh(force = false) {
      if (!this.isConnected || !this.wantedVariant || (!force && !this.isVisible())) return;
      const number = ++this.requestNumber;
      const variant = this.wantedVariant;
      try {
        const url = new URL(this.state.productUrl, window.location.origin);
        if (url.origin !== window.location.origin) return;
        url.searchParams.set('variant', variant);
        url.searchParams.set('section_id', 'catarette-deal-state');
        const current = new URL(window.location.href);
        const view = current.pathname === url.pathname ? current.searchParams.get('view') : this.state.view;
        if (view) url.searchParams.set('view', view);
        const next = await fetchState(url);
        if (!this.isConnected || number !== this.requestNumber || variant !== this.wantedVariant) return;
        if (String(next.variantId) !== variant || String(next.productId) !== this.productId) throw new Error('Mismatched deal variant');
        if (typeof next.active !== 'boolean' || !Number.isFinite(next.serverNow) || !Number.isFinite(next.end)) throw new Error('Invalid deal state');
        this.pending = false;
        this.lastSync = Date.now();
        this.clockOffset = next.serverNow * 1000 - Date.now();
        this.applyState(next);
      } catch {
        if (number === this.requestNumber) this.tick();
      }
    }

    applyState(state) {
      this.state = state;
      this.dataset.state = JSON.stringify(state);
      const text = (selector, value) => {
        const node = this.querySelector(selector);
        if (node) node.textContent = value;
      };
      text('[data-deal-price]', state.price);
      text('[data-deal-variant]', state.variantLabel);
      text('[data-deal-badge]', state.badge);
      const link = this.querySelector('[data-deal-link]');
      if (link) link.href = state.url;
      const stockValid = state.stockValid === true && Number.isFinite(state.remaining) && state.remaining >= 1 && state.remaining <= 100;
      const stock = this.querySelector('[data-deal-stock]');
      if (stock) stock.hidden = !stockValid;
      if (stockValid) {
        const remaining = Math.ceil(state.remaining);
        const used = 100 - remaining;
        text('[data-deal-remaining]', this.dataset.remainingText.replace('__PERCENT__', String(remaining)));
        const progress = this.querySelector('[data-deal-progress]');
        progress?.setAttribute('aria-valuenow', String(used));
        if (progress?.firstElementChild) progress.firstElementChild.style.width = `${used}%`;
      }
      const deadline = this.querySelector('[data-deal-deadline]');
      if (deadline) deadline.hidden = !(state.end > 0);
      const time = deadline?.querySelector('time');
      if (time && state.end > 0) {
        const date = new Date(state.end * 1000);
        time.dateTime = date.toISOString();
        const lang = document.documentElement.lang.startsWith('en') ? 'en-GB' : 'de-DE';
        time.textContent = `${new Intl.DateTimeFormat(lang, {timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'}).format(date)} · ${this.dataset.berlinText}`;
      }
      this.tick();
    }

    tick() {
      const state = this.state;
      const seconds = Math.max(0, Math.floor((state.end * 1000 - Date.now() - this.clockOffset) / 1000));
      const expired = state.end > 0 && seconds <= 0;
      const stale = Date.now() - this.lastSync > FRESH_FOR;
      this.hidden = !state.active || this.pending || expired || stale;
      const clock = this.querySelector('[data-deal-clock]');
      if (!clock) return;
      clock.hidden = !(state.end > 0) || expired;
      if (clock.hidden) return;
      const values = [Math.floor(seconds / 86400), Math.floor(seconds / 3600) % 24, Math.floor(seconds / 60) % 60, seconds % 60];
      ['days', 'hours', 'minutes', 'seconds'].forEach((key, i) => {
        const part = this.querySelector(`[data-clock-${key}]`);
        if (part) part.textContent = String(values[i]).padStart(2, '0');
      });
      clock.setAttribute('aria-label', this.dataset.countdownText.replace('__D__', values[0]).replace('__H__', values[1]).replace('__M__', values[2]).replace('__S__', values[3]));
    }
  });
})();
